import * as React from 'react'
import { ComponentClass, FunctionComponent, ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { Field, makeStyles, mergeClasses } from '@fluentui/react-components'
import { FieldProps } from '@fluentui/react-field'
import { useTranslation } from 'react-i18next'
import type { Slot } from '@fluentui/react-utilities'
import { Label } from '@fluentui/react-label'

export const useFormikFieldStyles = makeStyles({
    noLabel: {
        gridTemplateColumns: '1fr',
        paddingLeft: '0',
    },
    tinyLabel: {
        gridTemplateColumns: '50px 1fr',
    },
})

export type FormikSubFieldProps<CP, Values> = Omit<FormikFieldProps<CP, Values>, 'component'>

export type FormikFieldProps<CP, Values> = Omit<FieldProps, 'label'> & {
    fieldName: keyof Values
    component: FunctionComponent<CP> | ComponentClass<CP>
    componentProps?: Partial<Omit<CP, 'component' | 'value'>>
    renderLabel?: Slot<typeof Label>
    labelKey?: string
    labelKeyProps?: object
    hintKey?: string
    hintKeyProps?: object

    /**
     * Deprecated, but still here so we don't have to change all forms and test them at once.
     * @deprecated
     */
    context?: any
}

export function findValue<Values>(values: Values, fieldName: string): any {
    const fieldPath = fieldName.split('.')
    if (fieldPath.length > 1) {
        return findValue(findPropertyValue(values, fieldPath[0], {}), fieldPath.slice(1).join('.'))
    }
    return findPropertyValue(values, fieldPath[0], '')
}

export function findPropertyValue<Values>(values: Values, fieldName: string, fallbackValue: any): any {
    const fieldPathRegex = /(\w*)(\[(\d+)])/g.exec(fieldName)

    if (fieldPathRegex?.length === 4) {
        const fieldPath = fieldPathRegex[1]
        const fieldPathIndex = fieldPathRegex[3]
        return (values?.[fieldPath] || [])[fieldPathIndex] || fallbackValue
    }
    return values?.[fieldName] || fallbackValue
}

export default function FormikField<CP, Values>(props: FormikFieldProps<CP, Values>): ReactElement {
    const styles = useFormikFieldStyles()
    const { t: translate } = useTranslation()

    const { values, errors, handleChange, handleBlur } = useFormikContext<Values>()

    const validationMessage = useMemo(() => findValue(errors, props.fieldName as string), [values, errors])
    const validationState = useMemo(() => (findValue(errors, props.fieldName as string) ? 'error' : 'none'), [values, errors])
    const currentValue = useMemo(() => {
        return findValue(values, props.fieldName as string)
    }, [values])

    return (
        <Field
            label={props.renderLabel ? props.renderLabel : props.labelKey ? translate(props.labelKey, { ...props.labelKeyProps }) : undefined}
            hint={props.hint ?? (props.hintKey ? translate(props.hintKey, { ...props.hintKeyProps }) : undefined)}
            {...props}
            validationMessage={validationMessage}
            validationState={validationState}
            className={mergeClasses(props.className, !props.renderLabel && !props.labelKey && styles.noLabel)}
        >
            <props.component
                required={props.required}
                onBlur={handleBlur(props.fieldName)}
                onChange={handleChange(props.fieldName)}
                value={currentValue}
                autoComplete="off"
                data-lpignore={'true'}
                {...(props.componentProps as any)}
            />
        </Field>
    )
}
